import { useCallback, useEffect, useRef, useState } from 'react';

import api from '../services/api';
import {
  GrupoResponsaveis, montarGrupos, Pessoa, Setor,
} from '../utils/responsaveis';

interface Config {
  ativo?: boolean;
  urlAdvogados: string;
  urlAssistentes?: string;
  todos: GrupoResponsaveis;
  estiloLista?: string;
}

interface Dados {
  advogados: Pessoa[];
  assistentes?: Pessoa[];
}

interface Advogado {
  advogado_id: number;
  advogado_nome: string;
  advogado_setor: string;
}

interface Assistente {
  administrador_id: number;
  administrador_nome: string;
  administrador_setor: string;
}

// Lista de responsáveis das telas de horas e agenda. Busca advogados e assistentes uma vez
// e recalcula localmente, sem nova chamada, quando o setor muda.
function useResponsaveis(config: Config) {
  const [inChargeOptions, setInChargeOptions] = useState<GrupoResponsaveis[]>([]);
  const configAtual = useRef(config);
  configAtual.current = config;
  const dados = useRef<Promise<Dados> | null>(null);
  const montado = useRef(true);

  useEffect(() => () => {
    montado.current = false;
  }, []);

  const carregar = useCallback((): Promise<Dados> => {
    if (!dados.current) {
      const { urlAdvogados, urlAssistentes } = configAtual.current;

      dados.current = Promise.all([
        api.get<Advogado[]>(urlAdvogados),
        urlAssistentes ? api.get<Assistente[]>(urlAssistentes) : Promise.resolve(null),
      ]).then(([advogados, assistentes]) => ({
        advogados: advogados.data.map((item) => ({
          id: item.advogado_id, nome: item.advogado_nome, setor: item.advogado_setor,
        })),
        assistentes: assistentes
          ? assistentes.data.map((item) => ({
            id: item.administrador_id, nome: item.administrador_nome, setor: item.administrador_setor,
          }))
          : undefined,
      })).catch((error) => {
        dados.current = null;
        throw error;
      });
    }

    return dados.current;
  }, []);

  const filterAttorneysInCharge = useCallback(async (setor: Setor) => {
    try {
      const { advogados, assistentes } = await carregar();
      if (!montado.current) return;

      const { todos, estiloLista } = configAtual.current;
      setInChargeOptions(montarGrupos({
        todos, advogados, assistentes, setor, estiloLista,
      }));
    } catch {
      // sem a lista, o dropdown fica vazio; tentar de novo na próxima troca de setor
    }
  }, [carregar]);

  const ativo = config.ativo ?? true;

  useEffect(() => {
    if (ativo) filterAttorneysInCharge('all');
  }, [ativo, filterAttorneysInCharge]);

  return { inChargeOptions, filterAttorneysInCharge };
}

export default useResponsaveis;
